import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";
import {
    useGetCountriesQuery,
    useGetStatesQuery,
    useProfileSetupMutation,
} from "../redux/services/companyApi";
import { setProfileField, selectProfile } from "../redux/slices/profileSlice";
import { toast } from "sonner";
import { setAuthUser } from "../redux/slices/authSlice";
import { getApiErrorMessage } from "../utils/apiError";

const DEFAULT_OPERATION_HOURS = [
    { fromDay: "Mondays", fromHour: "09:00", fromPeriod: "AM", toDay: "Fridays", toHour: "05:00", toPeriod: "PM" },
];

export const useProfileController = () => {
    const dispatch = useDispatch();
    const profile = useSelector(selectProfile);

    // step1 basic info
    const [companyName, setCompanyName] = useState(profile.company_name || "");
    const [companyPhone, setCompanyPhone] = useState(profile.company_phone || "");
    const [companyAddress, setCompanyAddress] = useState(profile.company_address || "");
    const [companyType, setCompanyType] = useState(profile.company_type || "");
    const [companyLogo, setCompanyLogo] = useState<File | string | null>(profile.company_logo || null);
    const [selectedCountry, setSelectedCountry] = useState(profile.country_id?.toString() || "");
    const [selectedState, setSelectedState] = useState(profile.state_id?.toString() || "");

    // step2
    const [selectedServices, setSelectedServices] = useState<string[]>(profile.service_ids || []);
    const [businessZone, setBusinessZone] = useState(profile.business_zone || "");
    const [hasOtherBranch, setHasOtherBranch] = useState(Boolean(profile.has_other_branch));
    const [operationHours, setOperationHours] = useState<any>(profile.operation_hours || DEFAULT_OPERATION_HOURS);

    // step3 kyc
    const [profileSetup, { isLoading: isSubmitting }] = useProfileSetupMutation();
    const [isModalOpen, setModalOpen] = useState(false);
    const [kycStatus, setKycStatus] = useState("");

    const [countryPage, setCountryPage] = useState(1);

    const {
        data: countriesResponse,
        isLoading: countriesLoading,
        isFetching: countriesFetching,
    } = useGetCountriesQuery({ page: countryPage, per_page: 50 });

    useEffect(() => {
        if (!countriesResponse?.meta || countriesFetching) return;
        const { current_page, last_page } = countriesResponse.meta;
        if (current_page < last_page) {
            setCountryPage((prev) => prev + 1);
        }
    }, [countriesResponse, countriesFetching]);

    const countries = countriesResponse?.data ?? [];

    const {
        data: statesData = [],
        isLoading: statesLoading,
        isFetching: statesFetching,
    } = useGetStatesQuery(selectedCountry, {
        skip: !selectedCountry,
    });

    const states = statesData;

    const handleCountryChange = (countryId: string) => {
        setSelectedCountry(countryId);
        setSelectedState("");
    };

    // save steps
    const saveStep1 = () => {
        dispatch(setProfileField({ field: "company_name", value: companyName }));
        dispatch(setProfileField({ field: "company_phone", value: companyPhone }));
        dispatch(setProfileField({ field: "company_address", value: companyAddress }));
        dispatch(setProfileField({ field: "company_type", value: companyType }));
        if (companyLogo) dispatch(setProfileField({ field: "company_logo", value: companyLogo }));
        dispatch(setProfileField({ field: "country_id", value: selectedCountry || null }));
        dispatch(setProfileField({ field: "state_id", value: selectedState || null }));
    };

    const saveStep2 = (opHours?: any) => {
        dispatch(setProfileField({ field: "service_ids", value: selectedServices }));
        dispatch(setProfileField({ field: "business_zone", value: businessZone }));
        dispatch(setProfileField({ field: "has_other_branch", value: hasOtherBranch ? 1 : 0 }));
        dispatch(setProfileField({ field: "operation_hours", value: opHours || operationHours }));
    };

    const toggleService = (service: string) => {
        if (selectedServices.includes(service)) setSelectedServices(selectedServices.filter((s) => s !== service));
        else setSelectedServices([...selectedServices, service]);
    };
    const removeService = (service: string) => setSelectedServices(selectedServices.filter((s) => s !== service));

    const handleBusinessZoneChange = (value: string) => {
        setBusinessZone(value);
        dispatch(setProfileField({ field: "business_zone", value }));
    };

    const sanitizeNin = (val: string) => (val ? val.replace(/\D/g, "").trim() : val);
    const sanitizeGeneric = (val: string) => (val ? val.trim() : val);

    const descriptionMapping: Record<string, string> = {
        "identity card": "National Identity Card",
        "driving license": "Driver's License",
        "voter card": "Voter's Identification Card",
        "business reg": "Business Registration Certificate",
        "tax id": "Tax Identification Document",
        "prove of address": "Proof of Address",
    };

    const submitProfile = async (kycValues?: Record<string, string>, isIndividual?: boolean) => {
        try {
            const formData = new FormData();

            formData.append("company_name", companyName || "");
            formData.append("company_phone", companyPhone || "");
            formData.append("company_address", companyAddress || "");
            formData.append("company_type", companyType || "");
            if (companyLogo instanceof File) formData.append("company_logo", companyLogo);
            else if (typeof companyLogo === "string" && companyLogo.startsWith("http")) formData.append("company_logo", companyLogo);

            formData.append("country_id", selectedCountry || "");
            formData.append("state_id", selectedState || "");
            formData.append("business_zone", businessZone || "");
            formData.append("has_other_branch", hasOtherBranch ? "1" : "0");
            formData.append("operation_hours", JSON.stringify(operationHours));
            selectedServices.forEach((id) => formData.append("service_ids[]", id));

            if (kycValues) {
                const commonMapping: Record<string, string> = {
                    nin: "identity card",
                    driversLicense: "driving license",
                    voterCard: "voter card",
                    proofOfAddress: "prove of address",
                };
                const companyExtraMapping: Record<string, string> = {
                    ...commonMapping,
                    businessReg: "business reg",
                    tin: "tax id",
                };

                const mapping = isIndividual ? commonMapping : companyExtraMapping;

                let added = false;
                for (const [key, document_type_name] of Object.entries(mapping)) {
                    const rawValue = kycValues[key];
                    if (rawValue && rawValue.trim() !== "") {
                        const value = key === "nin" ? sanitizeNin(rawValue) : sanitizeGeneric(rawValue);
                        formData.append("document_type_name", document_type_name);
                        formData.append("document_type_description", descriptionMapping[document_type_name] || document_type_name);
                        if (key === "nin") formData.append("nin", value);
                        if (key === "driversLicense") formData.append("license_number", value);
                        if (key === "voterCard") formData.append("voter_card_number", value);
                        if (key === "proofOfAddress") formData.append("proof_of_address", value);
                        if (!isIndividual && key === "businessReg") formData.append("business_reg_no", value);
                        if (!isIndividual && key === "tin") formData.append("tax_no", value);
                        added = true;
                    }
                }

                if (!added && isIndividual) {
                    toast.error("Please provide at least one KYC document (NIN, etc.)");
                    return;
                }
            }

            const signupToken = Cookies.get("token") || Cookies.get("onboardingToken");
            if (signupToken) formData.append("signup_token", signupToken);

            const response = await profileSetup(formData).unwrap();

            const user = response.data?.user;
            const token = signupToken || "";

            if (response.status === "success") {
                const entity = response.data?.entity || response.entity;
                if (entity) {
                    if (entity.company_logo) dispatch(setProfileField({ field: "company_logo", value: entity.company_logo }));
                    if (entity.operation_hours) dispatch(setProfileField({ field: "operation_hours", value: entity.operation_hours }));
                }
                setKycStatus("Approved");
                setModalOpen(true);
            } else {
                setKycStatus("Failed");
                toast.error(response.message || "KYC submission failed");
            }

            if (user && token) {
                dispatch(setAuthUser({ token, user }));
            }

            return response;
        } catch (error: any) {
            console.error("Error submitting profile:", error);
            setKycStatus("Failed");
            // toast.error(error?.data?.message);
            toast.error(getApiErrorMessage(error, "An error occurred during submission."));
            throw error;
        }
    };

    return {
        companyName, setCompanyName,
        companyAddress, setCompanyAddress,
        companyPhone, setCompanyPhone,
        companyType, setCompanyType,
        companyLogo, setCompanyLogo,
        selectedCountry,
        setSelectedCountry: handleCountryChange,
        selectedState, setSelectedState,
        countries,
        states,
        countriesLoading: countriesLoading || countriesFetching,
        statesLoading: statesLoading || statesFetching,
        selectedServices, toggleService, removeService,
        businessZone, handleBusinessZoneChange,
        hasOtherBranch, setHasOtherBranch,
        operationHours, setOperationHours,
        saveStep2, saveStep1, submitProfile,
        isSubmitting, isModalOpen, setModalOpen, kycStatus,
    };
};